import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Input, List, Modal, Spin, Tag, Typography, theme } from 'antd';
import type { InputRef } from 'antd';
import { UserOutlined, BankOutlined, CompassOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { appPath } from '../routes/paths';
import { useSearchContacts } from '../features/contacts/hooks/useSearchContacts';
import type { ContactSearchResult } from '../features/contacts/types';
import { contactDisplayName, contactSecondaryName, isOrganizationContactType } from '../features/contacts/contactType';
import { usePermissions } from '../features/permissions/PermissionsContext';
import { NAV_GROUPS } from '../features/permissions/navConfig';
import { ACTIVITIES_NAV_ITEM } from '../features/tasks/copy';

const { Text } = Typography;
const RECENT_KEY = 'cp.command.recent';

interface CommandItem {
  id: string;
  label: string;
  description: string;
  group: 'Recent' | 'Navigation' | 'Actions' | 'Contacts';
  icon: React.ReactNode;
  execute: () => void;
  isContact?: boolean;
  contact?: ContactSearchResult;
}

const CommandSearch: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentIds, setRecentIds] = useState<string[]>(() => {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as string[];
        if (Array.isArray(parsed)) {
          return parsed.slice(0, 8);
        }
      }
    } catch {
      /* ignore */
    }
    return [];
  });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<InputRef>(null);
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { hasPermission } = usePermissions();

  const { data: results = [], isFetching } = useSearchContacts(query);

  const remember = useCallback((id: string) => {
    setRecentIds((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, 8);
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const navItems: CommandItem[] = useMemo(
    () =>
      NAV_GROUPS.flatMap((group) =>
        group.children
          .filter((item) => !item.permission || hasPermission(item.permission))
          .map((item) => ({
            id: `route:${item.key}`,
            label: item.label,
            description: `Open ${item.label}`,
            group: 'Navigation' as const,
            icon: <CompassOutlined />,
            execute: () => navigate(item.key),
          }))
      ),
    [navigate, hasPermission]
  );

  const actionItems: CommandItem[] = useMemo(() => {
    const actions: Array<{ permission: string; item: CommandItem }> = [
      {
        permission: 'contacts.create',
        item: {
          id: 'action:new-contact',
          label: 'New Contact',
          description: 'Go to contacts and add one',
          group: 'Actions',
          icon: <ThunderboltOutlined />,
          execute: () => navigate(appPath('/contacts')),
        },
      },
      {
        permission: 'quotes.create',
        item: {
          id: 'action:new-document',
          label: 'New Document',
          description: 'Go to documents and add one',
          group: 'Actions',
          icon: <ThunderboltOutlined />,
          execute: () => navigate(appPath('/documents')),
        },
      },
      {
        permission: 'leads.create',
        item: {
          id: 'action:new-lead',
          label: 'New Lead',
          description: 'Go to leads to create lead',
          group: 'Actions',
          icon: <ThunderboltOutlined />,
          execute: () => navigate(appPath('/leads')),
        },
      },
      {
        permission: 'tasks.view',
        item: {
          id: 'action:tasks-followups',
          label: ACTIVITIES_NAV_ITEM,
          description: 'Open follow-ups',
          group: 'Actions',
          icon: <ThunderboltOutlined />,
          execute: () => navigate(appPath('/tasks')),
        },
      },
    ];
    return actions.filter((a) => hasPermission(a.permission)).map((a) => a.item);
  }, [navigate, hasPermission]);

  const contactItems: CommandItem[] = useMemo(
    () =>
      hasPermission('contacts.view')
        ? results.map((contact) => ({
        id: `contact:${contact.id}`,
        label: contactDisplayName(contact),
        description: [contactSecondaryName(contact), contact.jobTitle, contact.email, contact.phone].filter(Boolean).join(' · '),
        group: 'Contacts',
        icon: isOrganizationContactType(contact.contactType) ? <BankOutlined /> : <UserOutlined />,
        execute: () => navigate(appPath(`/kundali/${contact.id}`)),
        isContact: true,
        contact,
      }))
        : [],
    [navigate, results, hasPermission]
  );

  const nonContactItems = useMemo(() => [...navItems, ...actionItems], [actionItems, navItems]);

  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q.length === 0
      ? [...nonContactItems.filter((item) => recentIds.includes(item.id)), ...nonContactItems]
      : nonContactItems.filter((item) =>
          item.label.toLowerCase().includes(q) || item.description.toLowerCase().includes(q)
        );
    const dedupBase = base.filter((item, index) => base.findIndex((x) => x.id === item.id) === index);
    if (q.length === 0) return dedupBase.slice(0, 12);
    return [...dedupBase.slice(0, 8), ...contactItems.slice(0, 20)];
  }, [contactItems, nonContactItems, query, recentIds]);

  const selectedIndex = activeIndex >= visibleItems.length ? 0 : activeIndex;
  if (selectedIndex !== activeIndex) {
    setActiveIndex(selectedIndex);
  }

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        setActiveIndex(0);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onOpen = () => {
      setOpen(true);
      setActiveIndex(0);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('open-command-search', onOpen);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('open-command-search', onOpen);
    };
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setQuery(value.trim());
    }, 300);
  }, []);

  const handleSelect = useCallback(
    (item: CommandItem) => {
      remember(item.id);
      setOpen(false);
      setInputValue('');
      setQuery('');
      item.execute();
    },
    [remember]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setInputValue('');
    setQuery('');
    setActiveIndex(0);
  }, []);

  const handleInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (visibleItems.length === 0 ? 0 : (prev + 1) % visibleItems.length));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => {
        if (visibleItems.length === 0) return 0;
        return prev === 0 ? visibleItems.length - 1 : prev - 1;
      });
      return;
    }

    if (event.key === 'Enter') {
      if (visibleItems.length === 0) return;
      event.preventDefault();
      handleSelect(visibleItems[activeIndex] ?? visibleItems[0]);
    }
  }, [activeIndex, handleSelect, visibleItems]);

  function getInitials(name: string | null, company: string | null): string {
    const display = name ?? company ?? '?';
    const parts = display.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return display.slice(0, 2).toUpperCase();
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title={null}
      closable={false}
      width={580}
      afterOpenChange={(isOpen) => {
        if (isOpen) inputRef.current?.focus();
      }}
      styles={{
        body: { padding: 0 },
      }}
      style={{ borderRadius: token.borderRadiusLG, overflow: 'hidden' }}
    >
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            gap: 8,
          }}
        >
          <Input
            ref={inputRef}
            variant="borderless"
            placeholder="Type to navigate..."
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            style={{ flex: 1, fontSize: 15 }}
            size="large"
          />
          {isFetching && <Spin size="small" />}
          <Tag style={{ margin: 0, cursor: 'pointer', fontSize: 11 }} onClick={handleClose}>
            ESC
          </Tag>
        </div>

        {visibleItems.length === 0 && query.trim().length === 0 && (
          <div style={{ padding: '20px 16px', textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Type a route, action, or contact name or phone
            </Text>
          </div>
        )}

        {query.trim().length > 0 && !isFetching && visibleItems.length === 0 && (
          <div style={{ padding: '20px 16px', textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              No results found for &ldquo;{query}&rdquo;
            </Text>
          </div>
        )}

        {visibleItems.length > 0 && (
          <List
            style={{ maxHeight: 420, overflowY: 'auto' }}
            dataSource={visibleItems}
            renderItem={(item, index) => (
              <List.Item
                key={item.id}
                onClick={() => handleSelect(item)}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                className={`cp-command-item ${index === activeIndex ? 'cp-command-item-active' : ''}`}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      size={36}
                      icon={item.icon}
                      style={{
                        backgroundColor:
                          item.group === 'Contacts' && isOrganizationContactType(item.contact?.contactType)
                            ? token.colorPrimaryBg
                            : item.group === 'Actions'
                              ? token.colorWarningBg
                              : token.colorSuccessBg,
                        color:
                          item.group === 'Contacts' && isOrganizationContactType(item.contact?.contactType)
                            ? token.colorPrimary
                            : item.group === 'Actions'
                              ? token.colorWarning
                              : token.colorSuccess,
                      }}
                    >
                      {item.isContact ? getInitials(item.contact?.displayName ?? null, item.contact?.companyName ?? null) : item.label.slice(0, 2).toUpperCase()}
                    </Avatar>
                  }
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Text strong style={{ fontSize: 14 }}>
                        {item.label}
                      </Text>
                      <Tag
                        color={item.group === 'Contacts' ? (isOrganizationContactType(item.contact?.contactType) ? 'blue' : 'green') : 'default'}
                        style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}
                      >
                        {item.group}
                      </Tag>
                    </div>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.description || 'No details'}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        )}

        <div
          style={{
            padding: '8px 16px',
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            gap: 16,
          }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            <kbd
              style={{
                background: token.colorBgLayout,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: 3,
                padding: '1px 5px',
                fontSize: 11,
              }}
            >
              ↵
            </kbd>{' '}
            to execute
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            <kbd
              style={{
                background: token.colorBgLayout,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: 3,
                padding: '1px 5px',
                fontSize: 11,
              }}
            >
            ↑ ↓
            </kbd>{' '}
            to move
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default CommandSearch;
